import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Route, Routes } from 'react-router-dom';

import {
  useGetMerchantTeamsQuery,
  useLazyGetCommissionBaseQuery,
  useLazyGetMerchantTeamsQuery,
  useLazyGetPaymentSystemConfigQuery,
  useLazyGetTeamBankAccountQuery,
  useLazyGetTeamBannerQuery,
  useLazyGetTeamHelpQuery,
  useLazyGetUserDetailsQuery
} from 'containers/app/api';
import DefaultPrivateLayout from './DefaultPrivateLayout';
import {
  selectCurrentTeam,
  selectIsOwner,
  selectMerchantCustomizations,
  selectPaymentSystemConfig,
  selectUserDetails
} from 'containers/app/appSlice';
import { PaymentSystem } from 'containers/payouts/constants';
import { useAppSelector } from 'hooks';
import { FireBaseCollections, db } from 'utils/firebase';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { clevertapPushProfile } from 'utils/analytics';
import { FirebaseStorage } from 'services/firebase';
import { loadGoogleMapsScript } from 'utils/maps';
import { WhatsNewModal } from 'components';
import { useLazyGetMerchantCustomizationsQuery } from 'containers/settings/api';
import { useLazyGetLegalReacceptanceContentQuery } from 'containers/legal-reacceptance/api';
import { LegalReacceptanceFlow } from 'containers/legal-reacceptance';
import { MAX_WALLET_BANK_SCREEN_VIEWS } from 'containers/legal-reacceptance/constants';
import { getWalletBankScreenMaxShowCount } from 'utils/firebase/remoteConfig';
import { WalletBankViewSource } from 'containers/legal-reacceptance/legalReacceptance.model';
import {
  useLazyGetFsmSubscriptionQuery,
  useLazyListSubscriptionPlansQuery
} from 'containers/integrations/fsm-integration/api';
import { usePreloadDispatchMap } from 'containers/dispatch/hooks/usePreloadDispatchMap';

// This layer in routing serves to support new layouts as we scale up
const PrivateLayout: FC<{ isLoadingInitAPI: boolean }> = ({ isLoadingInitAPI }) => {
  const { isLoading: isLoadingGetMerchantTeams } = useGetMerchantTeamsQuery();
  const [getTeamHelpQuery] = useLazyGetTeamHelpQuery();
  const [getMerchantTeamsQuery, getMerchantTeamsQueryState] = useLazyGetMerchantTeamsQuery();
  const [getUserDetailsQuery, getUserDetailsQueryState] = useLazyGetUserDetailsQuery();
  const [getPayoutsBannerQuery, getPayoutsBannerQueryState] = useLazyGetTeamBannerQuery();
  const [getPaymentSystemConfigQuery, getPaymentSystemConfigState] = useLazyGetPaymentSystemConfigQuery();
  const [getTeamBankAccountQuery, getTeamBankAccountState] = useLazyGetTeamBankAccountQuery();
  const [getMerchantCustomizationsQuery] = useLazyGetMerchantCustomizationsQuery();
  const [getCommissionBaseQuery] = useLazyGetCommissionBaseQuery();
  const [getFsmSubscriptionQuery] = useLazyGetFsmSubscriptionQuery();
  const [listSubscriptionPlansQuery] = useLazyListSubscriptionPlansQuery();

  const [getLegalReacceptanceContent, { data: legalReacceptanceContent }] = useLazyGetLegalReacceptanceContentQuery();

  const currentUser = useAppSelector(selectUserDetails);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isOwner = useAppSelector(selectIsOwner);
  const merchantCustomizations = useAppSelector(selectMerchantCustomizations);
  const paymentSystemConfig = useAppSelector(selectPaymentSystemConfig);
  const [showLegalReacceptanceModal, setShowLegalReacceptanceModal] = useState<boolean>(false);
  const [lastUpdatedPermissionAt, setLastUpdatedPermissionAt] = useState<number>(0);
  const [showWhatsNewModal, setShowWhatsNewModal] = useState<boolean>(false);
  const [hasConnectStatusChanged, setHasConnectStatusChanged] = useState<boolean>(false);
  const [hasBankStatusChanged, setHasBankStatusChanged] = useState<boolean>(false);
  const [hasPermissionsStateChanged, setHasPermissionsStateChanged] = useState<boolean>(false);
  const [hasUserFlagsStateChanged, setHasUserFlagsStateChanged] = useState<boolean>(false);
  const [hasEstimatesStateChanged, setHasEstimatesStateChanged] = useState<boolean>(false);
  const [hasBlockedStatusChanged, setHasBlockedStatusChanged] = useState<boolean>(false);
  const [customizationLastUpdatedAt, setCustomizationLastUpdatedAt] = useState<number>(0);
  const [walletBankScreenShownCount, setWalletBankScreenShownCount] = useState(0);
  const [walletBankScreenMaxShowCount, setWalletBankScreenMaxShowCount] = useState(MAX_WALLET_BANK_SCREEN_VIEWS);
  const [walletReminderDismissed, setWalletReminderDismissed] = useState(false);
  const [hasLoadedWalletCounterForTeam, setHasLoadedWalletCounterForTeam] = useState(false);
  const [hasLoadedCustomizationsForTeam, setHasLoadedCustomizationsForTeam] = useState(false);
  const [shouldCollectWABankDetails, setShouldCollectWABankDetails] = useState(false);
  const [isWalletBankReminderModalOpen, setIsWalletBankReminderModalOpen] = useState(false);
  const prevShowWalletReminderRef = useRef(false);

  usePreloadDispatchMap();

  // const location = useLocation();

  useEffect(() => {
    if (currentUser) {
      clevertapPushProfile({
        Site: {
          Phone: currentUser?.phone
        }
      });
    }
  }, [currentUser]);

  useEffect(() => {
    loadGoogleMapsScript();
    getCommissionBaseQuery();
  }, []);

  useEffect(() => {
    let isCancelled = false;
    const loadWalletBankScreenMaxShowCount = async () => {
      const maxShowCount = await getWalletBankScreenMaxShowCount();
      if (!isCancelled) {
        setWalletBankScreenMaxShowCount(maxShowCount);
      }
    };
    void loadWalletBankScreenMaxShowCount();
    return () => {
      isCancelled = true;
    };
  }, []);

  const readInvoiceDiscountWhatsNewFromFirestore = async ({
    merchantId,
    shouldCancel
  }: {
    merchantId: string;
    shouldCancel: () => boolean;
  }) => {
    try {
      const hasSeen = await FirebaseStorage.hasSeenInvoiceDiscountCalculationWhatsNew({ merchantId });
      if (shouldCancel()) {
        return;
      }
      if (!hasSeen) {
        setShowWhatsNewModal(true);
      }
    } catch (error) {
      void error;
    }
  };

  useEffect(() => {
    let unsubscribe = () => null;
    let isCancelled = false;
    if (currentUser?.id) {
      const merchantId = currentUser.id;
      unsubscribe = onSnapshot(doc(db, FireBaseCollections.Merchants.collectionName, currentUser.id), document => {
        const merchantDocData = document.data();
        const lastUpdatedAtFromDoc = merchantDocData?.[FireBaseCollections.Merchants.fields.permissionLastUpdatedAt];
        if (
          merchantDocData?.[FireBaseCollections.Merchants.fields.permissionUpdatedWeb] ||
          lastUpdatedAtFromDoc > lastUpdatedPermissionAt
        ) {
          getMerchantTeamsQuery();
          setLastUpdatedPermissionAt(lastUpdatedAtFromDoc);
          setHasPermissionsStateChanged(true);
        }
        if (merchantDocData?.[FireBaseCollections.Merchants.fields.userFlagsUpdated]) {
          getUserDetailsQuery({});
          setHasUserFlagsStateChanged(true);
        }
      });
      void readInvoiceDiscountWhatsNewFromFirestore({
        merchantId,
        shouldCancel: () => isCancelled || currentUser?.id !== merchantId
      });
    }
    return () => {
      isCancelled = true;
      unsubscribe();
    };
  }, [currentUser?.id]);

  useEffect(() => {
    let unsubscribe = () => null;
    let unsubscribeConnectStatusListener = () => null;
    let unsubscribeBankStatusListener = () => null;
    if (currentTeam?.id) {
      const collectionFieldName = `${currentTeam.id}_banner_web`;
      unsubscribe = onSnapshot(doc(db, FireBaseCollections.Merchants.collectionName, currentUser.id), document => {
        const hasDataChanged = document.data()?.[collectionFieldName];
        if (hasDataChanged) {
          getPayoutsBannerQuery(currentTeam.id);
        }
      });
      const connectStatusCollectionFieldName = `${currentTeam.id}_ca_application_status_change_web`;
      unsubscribeConnectStatusListener = onSnapshot(
        doc(db, FireBaseCollections.Merchants.collectionName, currentUser.id),
        document => {
          const hasDataChanged = document.data()?.[connectStatusCollectionFieldName];
          if (hasDataChanged) {
            getMerchantTeamsQuery();
            setHasConnectStatusChanged(true);
          }
        }
      );
      const bankStatusCollectionFieldName = `${currentTeam.id}_bank_account_update_web`;
      unsubscribeBankStatusListener = onSnapshot(
        doc(db, FireBaseCollections.Merchants.collectionName, currentUser.id),
        document => {
          const hasDataChanged = document.data()?.[bankStatusCollectionFieldName];
          if (hasDataChanged) {
            getPaymentSystemConfigQuery(currentTeam?.id);
            getMerchantTeamsQuery();
            getTeamBankAccountQuery(currentTeam?.id);
            setHasBankStatusChanged(true);
          }
        }
      );
    }
    if (currentTeam?.id) {
      getTeamHelpQuery({ teamId: currentTeam?.id });
    }
    return () => {
      unsubscribe();
      unsubscribeConnectStatusListener();
      unsubscribeBankStatusListener();
    };
  }, [currentTeam?.id]);

  useEffect(() => {
    let unsubscribeEstimatesStatusListener = () => null;
    let unsubscribePermissionStatusListener = () => null;
    let unsubscribeFSMStatusListener = () => null;
    let unsubscribeReAcceptanceListener = () => null;
    if (currentTeam?.id) {
      unsubscribeEstimatesStatusListener = onSnapshot(
        doc(db, FireBaseCollections.Teams.collectionName, currentTeam.id.toString()),
        document => {
          const hasDataChanged = document.data()?.[FireBaseCollections.Teams.fields.estimateStatusUpdated];
          if (hasDataChanged) {
            getMerchantTeamsQuery();
            setHasEstimatesStateChanged(true);
          }
        }
      );
      unsubscribePermissionStatusListener = onSnapshot(
        doc(db, FireBaseCollections.Teams.collectionName, currentTeam.id.toString()),
        document => {
          const hasDataChanged = document.data()?.[FireBaseCollections.Teams.fields.permissionUpdatedAtWeb];
          if (hasDataChanged) {
            getMerchantTeamsQuery();
            setHasBlockedStatusChanged(true);
          }
        }
      );
      unsubscribeFSMStatusListener = onSnapshot(
        doc(db, FireBaseCollections.Teams.collectionName, currentTeam.id.toString()),
        document => {
          const changedTime = document.data()?.[FireBaseCollections.Teams.fields.customizationLastUpdatedAt];
          setCustomizationLastUpdatedAt(changedTime ? changedTime : 0);
          const changedCustomizationData = document.data()?.[FireBaseCollections.Teams.fields.customizationUpdatedWeb];
          if (changedCustomizationData === 'fsm-subscription' && changedTime > customizationLastUpdatedAt) {
            getFsmSubscriptionQuery({ teamId: currentTeam.id.toString() });
            listSubscriptionPlansQuery({ teamId: currentTeam.id });
          }
        }
      );
      unsubscribeReAcceptanceListener = onSnapshot(
        doc(db, FireBaseCollections.Teams.collectionName, currentTeam.id.toString()),
        async document => {
          const data = document.data();
          const rawWalletCount = data?.[FireBaseCollections.Teams.fields.legalReacceptanceWalletBankScreenShownCount];
          const shouldCollectWABankDetailsFromFirestore =
            data?.[FireBaseCollections.Teams.fields.collectWABankDetails] ?? false;
          setWalletBankScreenShownCount(
            typeof rawWalletCount === 'number' && !Number.isNaN(rawWalletCount) ? rawWalletCount : 0
          );
          setShouldCollectWABankDetails(Boolean(shouldCollectWABankDetailsFromFirestore));
          setHasLoadedWalletCounterForTeam(true);
          const changedTime = data?.[FireBaseCollections.Teams.fields.customizationLastUpdatedAt];
          setCustomizationLastUpdatedAt(changedTime ? changedTime : 0);
          const changedCustomizationData = data?.[FireBaseCollections.Teams.fields.customizationUpdatedWeb];
          if (
            changedCustomizationData === 'wallet-eligible-bank-account-submitted' &&
            changedTime > customizationLastUpdatedAt
          ) {
            await Promise.all([
              getMerchantCustomizationsQuery({ teamId: currentTeam.id.toString() }),
              getTeamBankAccountQuery(currentTeam.id),
              getPaymentSystemConfigQuery(currentTeam.id)
            ]);
          }
          if (changedCustomizationData === 're-acceptance-required' && changedTime > customizationLastUpdatedAt) {
            await Promise.all([
              getMerchantCustomizationsQuery({ teamId: currentTeam.id.toString() }),
              getTeamBankAccountQuery(currentTeam.id),
              getPaymentSystemConfigQuery(currentTeam.id)
            ]);
          }
        }
      );
    }
    return () => {
      unsubscribeEstimatesStatusListener();
      unsubscribePermissionStatusListener();
      unsubscribeFSMStatusListener();
      unsubscribeReAcceptanceListener();
    };
  }, [currentTeam?.id]);

  useEffect(() => {
    if (getPayoutsBannerQueryState.isSuccess) {
      FirebaseStorage.updateMerchantCollectionFieldValue({
        merchantId: currentUser.id,
        collectionFieldName: `${currentTeam.id}_banner_web`,
        value: false
      });
    }
  }, [getPayoutsBannerQueryState.isFetching, currentTeam?.id]);

  useEffect(() => {
    if (getPaymentSystemConfigState.isSuccess && getTeamBankAccountState.isSuccess) {
      if (hasBankStatusChanged) {
        FirebaseStorage.updateMerchantCollectionFieldValue({
          merchantId: currentUser.id,
          collectionFieldName: `${currentTeam.id}_bank_account_update_web`,
          value: false
        });
        setHasBankStatusChanged(false);
      }
    }
  }, [getPaymentSystemConfigState.isFetching, currentTeam?.id, getTeamBankAccountState.isFetching]);

  const updateFirebaseDoc = useCallback(async () => {
    const merchantCollectionRef = doc(db, FireBaseCollections.Merchants.collectionName, currentUser?.id);
    await updateDoc(merchantCollectionRef, {
      PermissionUpdatedWeb: false
    });
  }, [currentUser?.id]);

  useEffect(() => {
    if (getMerchantTeamsQueryState?.isSuccess && currentUser?.id) {
      if (hasPermissionsStateChanged) {
        updateFirebaseDoc();
        setHasPermissionsStateChanged(false);
      }
      if (hasConnectStatusChanged) {
        FirebaseStorage.updateMerchantCollectionFieldValue({
          merchantId: currentUser.id,
          collectionFieldName: `${currentTeam.id}_ca_application_status_change_web`,
          value: false
        });
        setHasConnectStatusChanged(false);
      }
      if (hasEstimatesStateChanged) {
        FirebaseStorage.updateTeamsCollectionFieldValue({
          teamId: currentTeam.id.toString(),
          collectionFieldName: FireBaseCollections.Teams.fields.estimateStatusUpdated,
          value: false
        });
        setHasEstimatesStateChanged(false);
      }
      if (hasBlockedStatusChanged) {
        FirebaseStorage.updateTeamsCollectionFieldValue({
          teamId: currentTeam.id.toString(),
          collectionFieldName: FireBaseCollections.Teams.fields.permissionUpdatedAtWeb,
          value: false
        });
        setHasBlockedStatusChanged(false);
      }
    }
  }, [getMerchantTeamsQueryState?.isFetching, updateFirebaseDoc]);

  useEffect(() => {
    if (getUserDetailsQueryState?.isSuccess && currentUser?.id && hasUserFlagsStateChanged) {
      FirebaseStorage.updateMerchantCollectionFieldValue({
        merchantId: currentUser.id,
        collectionFieldName: FireBaseCollections.Merchants.fields.userFlagsUpdated,
        value: false
      });
      setHasUserFlagsStateChanged(false);
    }
  }, [getUserDetailsQueryState?.isFetching, currentUser?.id, hasUserFlagsStateChanged]);

  // const showGenericMobileView =
  //   (isAndroid || isIOS) && !isTablet && !(location.pathname === routesPath.LENDICA_WEBVIEW);

  const onDismissWhatsNewModal = () => {
    if (currentUser.id) {
      FirebaseStorage.markInvoiceDiscountCalculationWhatsNewAsSeen({ merchantId: currentUser.id });
    }
    setShowWhatsNewModal(false);
  };

  const hasWalletEligibleBankAccount = merchantCustomizations?.hasWalletEligibleBankAccount ?? false;

  const isLegalReacceptanceRequired =
    merchantCustomizations?.pendingActions?.legalAgreementReAcceptanceRequired ?? false;

  const refreshTeamDataAfterLegal = () => {
    if (!currentTeam?.id) return;
    getMerchantCustomizationsQuery({ teamId: currentTeam.id.toString() });
    getPaymentSystemConfigQuery(currentTeam.id);
    getTeamBankAccountQuery(currentTeam.id);
  };

  const handleLegalReacceptanceClose = () => {
    setShowLegalReacceptanceModal(false);
    setWalletReminderDismissed(true);
  };

  const handleWalletBankReminderClose = () => {
    setWalletReminderDismissed(true);
    setIsWalletBankReminderModalOpen(false);
  };

  const isWAPaymentSystem = paymentSystemConfig?.paymentSystem === PaymentSystem.WA;

  /** Bank reminder only for WA payment system, no eligible wallet, and flag or Firestore counter nudge. */
  const walletBankReminderShouldPrompt =
    isOwner &&
    Boolean(currentTeam?.id) &&
    isWAPaymentSystem &&
    !hasWalletEligibleBankAccount &&
    !showLegalReacceptanceModal &&
    (shouldCollectWABankDetails || walletBankScreenShownCount <= walletBankScreenMaxShowCount);
  const walletBankReminderViewSource: WalletBankViewSource = 'auto_modal';

  const shouldOpenWalletBankReminderModal =
    walletBankReminderShouldPrompt &&
    hasLoadedWalletCounterForTeam &&
    hasLoadedCustomizationsForTeam &&
    !walletReminderDismissed;

  useEffect(() => {
    setWalletReminderDismissed(true);
    setIsWalletBankReminderModalOpen(false);
    setHasLoadedWalletCounterForTeam(false);
    setHasLoadedCustomizationsForTeam(false);
    setShouldCollectWABankDetails(false);
    setWalletBankScreenShownCount(0);
    prevShowWalletReminderRef.current = false;
  }, [currentTeam?.id]);

  useEffect(() => {
    if (!currentTeam?.id) {
      return;
    }
    if (isWalletBankReminderModalOpen && !prevShowWalletReminderRef.current) {
      void FirebaseStorage.incrementTeamsCollectionField({
        teamId: currentTeam.id.toString(),
        collectionFieldName: FireBaseCollections.Teams.fields.legalReacceptanceWalletBankScreenShownCount
      });
    }
    prevShowWalletReminderRef.current = isWalletBankReminderModalOpen;
  }, [isWalletBankReminderModalOpen, currentTeam?.id]);

  useEffect(() => {
    if (shouldOpenWalletBankReminderModal) {
      setIsWalletBankReminderModalOpen(true);
    }
  }, [shouldOpenWalletBankReminderModal]);

  useEffect(() => {
    if (showLegalReacceptanceModal) {
      setIsWalletBankReminderModalOpen(false);
    }
  }, [showLegalReacceptanceModal]);

  useEffect(() => {
    let isCancelled = false;
    if (!currentTeam?.id) {
      return () => {
        isCancelled = true;
      };
    }
    const teamId = currentTeam.id.toString();
    const loadMerchantCustomizationsForTeam = async () => {
      try {
        await getMerchantCustomizationsQuery({ teamId }).unwrap();
      } finally {
        if (!isCancelled) {
          setHasLoadedCustomizationsForTeam(true);
          setWalletReminderDismissed(false);
        }
      }
    };
    void loadMerchantCustomizationsForTeam();
    return () => {
      isCancelled = true;
    };
  }, [currentTeam?.id]);

  // Fetch legal reacceptance content when prompt is required (owners only)
  useEffect(() => {
    if (isLegalReacceptanceRequired && isOwner) {
      getLegalReacceptanceContent();
    }
  }, [isLegalReacceptanceRequired, isOwner]);

  useEffect(() => {
    if (legalReacceptanceContent && isLegalReacceptanceRequired && isOwner) {
      setShowLegalReacceptanceModal(true);
    }
  }, [legalReacceptanceContent, isLegalReacceptanceRequired, isOwner]);

  // TODO Remove Generic Mobile View once mobile responsiveness of the app is complete
  return (
    <div>
      <Routes>
        <Route
          path="*"
          element={
            // showGenericMobileView ? (
            //   <GenericMobileView />
            // ) : (
            <DefaultPrivateLayout
              isLoadingInitAPI={isLoadingInitAPI}
              isLoadingGetMerchantTeams={isLoadingGetMerchantTeams}
            />
            // )
          }
        />
      </Routes>
      {showWhatsNewModal && (
        // && !showGenericMobileView
        <WhatsNewModal
          config={{ open: showWhatsNewModal }}
          onPrimaryBtnClick={onDismissWhatsNewModal}
          onClose={onDismissWhatsNewModal}
        />
      )}
      {showLegalReacceptanceModal && legalReacceptanceContent && isOwner && currentTeam?.id && (
        <LegalReacceptanceFlow
          open={showLegalReacceptanceModal}
          content={legalReacceptanceContent}
          teamId={currentTeam.id}
          walletBankScreenShownCount={walletBankScreenShownCount}
          walletBankScreenMaxShowCount={walletBankScreenMaxShowCount}
          forceShowWalletBankStep={shouldCollectWABankDetails}
          hasWalletEligibleBankAccount={hasWalletEligibleBankAccount}
          onRefreshTeamData={refreshTeamDataAfterLegal}
          onClose={handleLegalReacceptanceClose}
        />
      )}
      {isWalletBankReminderModalOpen && isOwner && currentTeam?.id && (
        <LegalReacceptanceFlow
          variant="walletReminder"
          open={isWalletBankReminderModalOpen}
          teamId={currentTeam.id}
          walletBankScreenShownCount={walletBankScreenShownCount}
          walletBankScreenMaxShowCount={walletBankScreenMaxShowCount}
          forceShowWalletBankStep={shouldCollectWABankDetails}
          hasWalletEligibleBankAccount={hasWalletEligibleBankAccount}
          walletBankViewSource={walletBankReminderViewSource}
          onRefreshTeamData={refreshTeamDataAfterLegal}
          onClose={handleWalletBankReminderClose}
        />
      )}
    </div>
  );
};

export default PrivateLayout;
